// Renders a fully-realistic sample audit dashboard at /sample-audit/:id.
// No auth wall, no paywall - these are public marketing surfaces.
//
// Hydrates sessionStorage with the sample's scrapedData (Dashboard reads
// from there for profile pic / banner / experience fields), then mounts
// the real Dashboard with isSample=true.

import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import Dashboard from './Dashboard';
import SEO from './SEO';
import { SAMPLE_AUDITS } from '../data/sampleAudits';

const SampleAuditViewer = () => {
  const { id } = useParams();
  const sample = SAMPLE_AUDITS[id];

  useEffect(() => {
    if (!sample) return;
    try {
      const urlKey = btoa(sample.profileUrl).replace(/[^a-zA-Z0-9]/g, '');
      sessionStorage.setItem(
        `scrapedProfileData_${urlKey}`,
        JSON.stringify(sample.scrapedData)
      );
    } catch (_) { /* sessionStorage may be disabled */ }
  }, [sample]);

  // Pre-compute the merged results object so Dashboard sees scrapedData on
  // first render too (in case sessionStorage write hasn't propagated).
  const enrichedResults = useMemo(() => {
    if (!sample) return null;
    return {
      ...sample.auditResults,
      // Mirror scraped fields onto results so any code reading from results
      // (avatar, banner_image, etc.) finds them without sessionStorage.
      ...sample.scrapedData,
      isSample: true,
    };
  }, [sample]);

  if (!sample) {
    // Unknown sample id - bounce to the static list page (full page nav).
    if (typeof window !== 'undefined') window.location.replace('/sample-audits');
    return null;
  }

  return (
    <>
      <SEO
        title={`Sample Audit: ${sample.meta.persona} | LinkedIn Profile Optimizer`}
        description={sample.meta.headline}
        canonical={`https://optimizer.growleads.io/sample-audit/${sample.id}`}
        type="article"
      />

      {/* Sample banner - clearly marks this as a demo so users don't think
          it's their own audit. Sticky so it stays visible during scroll. */}
      <div style={bannerStyle}>
        {/* Anchor tags (not React Router Link) - /sample-audits is a static
            HTML file served by Express, not a React route. Client-side
            navigation would hit the SPA catch-all and bounce to /. */}
        <a href="/sample-audits" style={backLinkStyle}>
          <ArrowLeft size={16} />
          <span>All sample audits</span>
        </a>
        <div style={bannerCenterStyle}>
          <AlertCircle size={16} color="#0369a1" />
          <span>
            <strong>Sample audit</strong> for {sample.meta.persona}
          </span>
        </div>
        <a href="/optimize" style={ctaLinkStyle}>
          Run my own audit →
        </a>
      </div>

      <Dashboard
        results={enrichedResults}
        profileUrl={sample.profileUrl}
        questionnaireData={sample.questionnaireData}
        onBackToHero={() => { window.location.href = '/sample-audits'; }}
        isSample={true}
      />
    </>
  );
};

const bannerStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 100,
  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
  borderBottom: '1px solid #bae6fd',
  padding: '10px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  fontSize: 14,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  flexWrap: 'wrap',
};
const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: '#0369a1',
  textDecoration: 'none',
  fontWeight: 500,
};
const bannerCenterStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: '#0c4a6e',
  flex: 1,
  justifyContent: 'center',
};
const ctaLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#0ea5e9',
  color: 'white',
  textDecoration: 'none',
  padding: '8px 16px',
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 13,
};

export default SampleAuditViewer;
